import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { ClassTimetableRows } from '../staff/services/staff-timetable.service';
import { ParentTimetableService } from './parent-timetable.service';
import {
  TimelinePeriod,
  calculateTimelineMetrics,
  getTimeTopOffset,
  isPastPeriod,
  isCurrentPeriod,
  nowToMinutes,
  getCurrentWeekDays,
  WeekDay,
} from '../staff/shared/staff-timetable-timeline';

const HOUR_HEIGHT = 72;

// weekDaysMap: numeric day index (Date.getDay(), 0=Sun..6=Sat) -> the
// response's per-day enabled flag field.
const WEEKDAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// The `periods` object is keyed by day NUMBER, Monday=1..Sunday=7 (ISO-style),
// unrelated to Date.getDay()'s Sunday=0 indexing.
const PERIODS_DAY_NUMBER: { [jsDay: number]: string } = {
  0: '7', 1: '1', 2: '2', 3: '3', 4: '4', 5: '5', 6: '6',
};

// Timetable — same /gettimetableclass endpoint and response shape the
// parent mobile app's TimeTable.js consumes (classId-scoped, isTimetable +
// per-day flags + periods keyed by day-number/period-number), and already
// implemented once for staff's Class Timetable screen — reuses that same
// service/shared timeline-layout engine and mirrors its UI (day chips +
// vertical hour timeline with positioned period cards) rather than
// duplicating either.
@Component({
  selector: 'app-parent-timetable',
  templateUrl: './parent-timetable.component.html',
  styleUrls: ['./parent-timetable.component.css'],
})
export class ParentTimetableComponent implements OnInit, OnChanges, OnDestroy {

  @Input() classId = '';

  loading = true;
  loadError = false;
  private loadRequestId = 0;
  isConfigured = true; // false when the API reports no timetable configured

  weekDays: WeekDay[] = getCurrentWeekDays();
  selectedDay: WeekDay = this.weekDays.find(d => d.isToday) || this.weekDays[0];

  timetableRows: ClassTimetableRows | null = null;
  periods: TimelinePeriod[] = [];
  hourTops: number[] = [];
  totalHeight = 24 * HOUR_HEIGHT;
  hourHeight = HOUR_HEIGHT;

  now = new Date();
  private nowTimer: any;
  private scrollTimer: any;

  constructor(private parentTimetableService: ParentTimetableService, private translate: TranslateService) { }

  ngOnInit(): void {
    this.nowTimer = setInterval(() => { this.now = new Date(); }, 60000);
    if (this.classId) {
      this.loadTimetable();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['classId'] && this.classId) {
      this.loadTimetable();
    }
  }

  ngOnDestroy(): void {
    if (this.nowTimer) { clearInterval(this.nowTimer); }
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
  }

  async loadTimetable() {
    if (!this.classId) { return; }
    this.loading = true;
    this.loadError = false;
    // Bumped on every call so a still-in-flight request for the PREVIOUS
    // student/class can't land after the new one and overwrite its fresher
    // data with stale data.
    const requestId = ++this.loadRequestId;
    try {
      const response: any = await firstValueFrom(this.parentTimetableService.getClassTimetable(this.classId));
      if (requestId !== this.loadRequestId) { return; }
      // TimetableConfigurationNotExist (per the parent mobile app's own
      // handling) means "no timetable configured" — not an error state.
      if (response && response.message && String(response.message).trim() === 'TimetableConfigurationNotExist') {
        this.isConfigured = false;
        this.timetableRows = null;
      } else if (response && response.rows && (response.rows as ClassTimetableRows).isTimetable) {
        this.isConfigured = true;
        this.timetableRows = response.rows as ClassTimetableRows;
      } else {
        this.loadError = true;
      }
    } catch (e) {
      if (requestId !== this.loadRequestId) { return; }
      this.loadError = true;
    } finally {
      if (requestId === this.loadRequestId) {
        this.loading = false;
        this.applySelectedDay();
      }
    }
  }

  selectDay(day: WeekDay): void {
    this.selectedDay = day;
    this.applySelectedDay();
  }

  private async applySelectedDay() {
    if (!this.timetableRows) {
      this.periods = [];
      this.hourTops = [];
      return;
    }
    const dayKey = WEEKDAY_KEYS[this.selectedDay.date.getDay()];
    const enabled = (this.timetableRows as any)[dayKey];
    const dayNumber = PERIODS_DAY_NUMBER[this.selectedDay.date.getDay()];
    const periodsForDay = enabled ? (this.timetableRows.periods[dayNumber] || {}) : {};
    const rawPeriods = Object.values(periodsForDay).filter((p: any) => p && p.periodStart && p.periodEnd);

    const translations = await firstValueFrom(this.translate.get([
      'breakPeriod', 'lunchBreakPeriod', 'noSubjectAssigned', 'noStaffAssigned'
    ]));

    const normalized: TimelinePeriod[] = rawPeriods.map((p: any) => ({
      periodId: String(p.periodId),
      periodStart: p.periodStart,
      periodEnd: p.periodEnd,
      title: p.periodType === 0 ? translations['breakPeriod'] : p.periodType === 2 ? translations['lunchBreakPeriod'] : (p.subject || translations['noSubjectAssigned']),
      subtitle: p.periodType === 1 ? (p.staffName || translations['noStaffAssigned']) : null,
      isBreak: p.periodType === 0 || p.periodType === 2,
    }));

    const metrics = calculateTimelineMetrics(normalized, this.hourHeight);
    this.periods = metrics.periods;
    this.hourTops = metrics.hourTops;
    this.totalHeight = metrics.totalHeight;

    this.scheduleAutoScroll();
  }

  private scheduleAutoScroll(): void {
    if (this.scrollTimer) { clearTimeout(this.scrollTimer); }
    this.scrollTimer = setTimeout(() => {
      const container = document.getElementById('pt-timeline-scroll');
      if (!container) { return; }
      let offset: number;
      if (this.selectedDay.isToday) {
        offset = getTimeTopOffset(this.currentTimeString, this.hourHeight);
      } else if (this.periods.length) {
        offset = this.periods[0].top || 0;
      } else {
        return;
      }
      container.scrollTo({ top: Math.max(offset - 100, 0), behavior: 'smooth' });
    }, 600);
  }

  get currentTimeString(): string {
    const h = String(this.now.getHours()).padStart(2, '0');
    const m = String(this.now.getMinutes()).padStart(2, '0');
    return `${h}:${m}`;
  }

  get currentTimeLabel(): string {
    const hours24 = this.now.getHours();
    const m = String(this.now.getMinutes()).padStart(2, '0');
    const period = hours24 >= 12 ? 'PM' : 'AM';
    const hours12 = hours24 % 12 || 12;
    return `${hours12}:${m} ${period}`;
  }

  get nowOffset(): number {
    return getTimeTopOffset(this.currentTimeString, this.hourHeight);
  }

  get showNowLine(): boolean {
    return this.selectedDay.isToday;
  }

  hourLabel(hour: number): string {
    if (hour === 0 || hour === 24) { return '12 AM'; }
    if (hour === 12) { return '12 PM'; }
    return hour < 12 ? `${hour} AM` : `${hour - 12} PM`;
  }

  isPast(p: TimelinePeriod): boolean {
    return isPastPeriod(this.selectedDay.isToday, p.periodEnd, nowToMinutes(this.now));
  }

  isCurrent(p: TimelinePeriod): boolean {
    return isCurrentPeriod(this.selectedDay.isToday, p.periodStart, p.periodEnd, nowToMinutes(this.now));
  }
}
